// GuidanceProvider — a lightweight, zero-cost (when idle) guidance context.
//
// Wraps the whole app and gives any descendant the ability to:
//   - Open / close the player home tour overlay
//   - Track the current step index for the player tour
//
// Design constraints
// ------------------
// 1. Zero cost when idle: pure React state, no effects, no network calls,
//    no layout work. Nothing executes until a consumer opens a tour.
// 2. Single source of truth: the player tour's open state lives here so
//    the home page and any future "Show me around" entrypoint share it.
// 3. Provider must sit ABOVE the router so route changes can't remount it
//    and lose in-progress tour state.
//
// Usage
// -----
//   // In any descendant:
//   const { playerTourOpen, openPlayerTour, closePlayerTour } = useGuidance();

import { createContext, useContext, useState, ReactNode } from "react";

export const PLAYER_HOME_TOUR_KEY = "jingle:tour:playerHome:v1";

interface GuidanceContextValue {
  playerTourKey: string;
  playerTourOpen: boolean;
  openPlayerTour: () => void;
  closePlayerTour: () => void;
  playerTourStep: number;
  setPlayerTourStep: (step: number) => void;
}

const GuidanceContext = createContext<GuidanceContextValue | null>(null);

export function GuidanceProvider({ children }: { children: ReactNode }) {
  const [playerTourOpen, setPlayerTourOpen] = useState(false);
  const [playerTourStep, setPlayerTourStep] = useState(0);

  function openPlayerTour() {
    setPlayerTourStep(0);
    setPlayerTourOpen(true);
  }

  function closePlayerTour() {
    setPlayerTourOpen(false);
  }

  return (
    <GuidanceContext.Provider
      value={{
        playerTourKey: PLAYER_HOME_TOUR_KEY,
        playerTourOpen,
        openPlayerTour,
        closePlayerTour,
        playerTourStep,
        setPlayerTourStep,
      }}
    >
      {children}
    </GuidanceContext.Provider>
  );
}

export function useGuidance(): GuidanceContextValue {
  const ctx = useContext(GuidanceContext);
  if (!ctx) {
    throw new Error("useGuidance must be used inside <GuidanceProvider>.");
  }
  return ctx;
}
